import React, { useState } from "react";
import { Info, Quote, Wrench, ChevronDown } from "lucide-react";

const DIMENSIONS = [
  { key: "content_completeness", label: "Content Completeness", testId: "health-dimension-card-content" },
  { key: "evidence_strength", label: "Evidence Strength", testId: "health-dimension-card-evidence" },
  { key: "role_alignment", label: "Role Alignment", testId: "health-dimension-card-alignment" },
  { key: "clarity", label: "Clarity", testId: "health-dimension-card-clarity" },
  { key: "ats_readability", label: "ATS Readability", testId: "health-dimension-card-ats" },
];

const barColor = (v) => (v >= 80 ? "bg-emerald-500" : v >= 50 ? "bg-amber-500" : "bg-rose-500");
const textColor = (v) => (v >= 80 ? "text-emerald-700" : v >= 50 ? "text-amber-600" : "text-rose-600");
const ringColor = (v) => (v >= 80 ? "#059669" : v >= 50 ? "#D97706" : "#E11D48");
const statusPill = (v) => (v >= 80
  ? { label: "Looking good", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" }
  : v >= 50
    ? { label: "Building", cls: "bg-amber-50 text-amber-700 border-amber-200" }
    : { label: "Needs attention", cls: "bg-rose-50 text-rose-600 border-rose-200" });

const Donut = ({ value }) => {
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="relative h-28 w-28 shrink-0" data-testid="resume-health-donut">
      <svg viewBox="0 0 36 36" className="h-28 w-28 -rotate-90">
        <circle cx="18" cy="18" r="15.5" fill="none" stroke="#EEF2F7" strokeWidth="4" />
        <circle cx="18" cy="18" r="15.5" fill="none" stroke={ringColor(pct)} strokeWidth="4" strokeLinecap="round"
                strokeDasharray={`${(pct / 100) * 97.4} 97.4`} style={{ transition: "stroke-dasharray 0.8s ease" }} />
      </svg>
      <span className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="num-display text-2xl leading-none">{pct}</span>
        <span className="mt-1 text-[9px] uppercase tracking-wide text-slate-400">overall</span>
      </span>
    </div>
  );
};

const Row = ({ label, data, testId }) => {
  const [open, setOpen] = useState(false);
  if (!data) return null;
  const score = data.score || 0;
  const pill = statusPill(score);
  return (
    <div data-testid={testId}>
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left group" data-testid={`${testId}-toggle`}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
            {label}
            {data.aligned_against && <span className="text-[10px] font-mono text-slate-400">vs {data.aligned_against}</span>}
          </span>
          <span className="flex shrink-0 items-center gap-2">
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${pill.cls}`}>{pill.label}</span>
            <span className={`num-display text-sm ${textColor(score)}`}>{score}%</span>
          </span>
        </div>
        <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full rounded-full ${barColor(score)}`} style={{ width: `${score}%`, transition: "width 0.7s ease" }} />
        </div>
      </button>
      {open ? (
        <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
          {data.confidence && <span className="inline-block rounded bg-white px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-slate-600 border border-slate-200">{data.confidence} confidence</span>}
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Why this score</p>
            <p className="mt-1 text-xs text-slate-700">{data.reason}</p>
          </div>
          {data.evidence && (
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Evidence in your resume</p>
              <p className="mt-1 flex gap-1.5 text-xs text-slate-700"><Quote className="w-3.5 h-3.5 shrink-0 text-sky-600 mt-0.5" /><span>{data.evidence}</span></p>
            </div>
          )}
          {data.improvement && (
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-emerald-700">Do this next</p>
              <p className="mt-1 flex gap-1.5 text-xs text-slate-700"><Wrench className="w-3.5 h-3.5 shrink-0 text-emerald-600 mt-0.5" /><span>{data.improvement}</span></p>
            </div>
          )}
        </div>
      ) : (
        <p className="mt-1.5 text-xs text-slate-500 line-clamp-1">{data.reason}</p>
      )}
    </div>
  );
};

const ResumeHealth = ({ health }) => {
  const [open, setOpen] = useState(true);
  const scores = DIMENSIONS.map((d) => health?.[d.key]?.score).filter((s) => typeof s === "number");
  const overall = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
  const pill = statusPill(overall);

  return (
    <div className="card-soft h-full overflow-hidden" data-testid="resume-health-container">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors" data-testid="resume-health-toggle">
        <span className="flex items-center gap-2">
          <h3 className="text-lg font-bold text-slate-900">Resume Health</h3>
          <span title="Heuristic guidance indicators, not guaranteed metrics. Each score is grounded in your resume evidence. Tap a dimension for detail." data-testid="health-info-tooltip">
            <Info className="w-3.5 h-3.5 text-slate-400" />
          </span>
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5">
          <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <Donut value={overall} />
            <div className="min-w-0">
              <span className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${pill.cls}`} data-testid="resume-health-overall-pill">{pill.label}</span>
              <p className="mt-2 text-xs text-slate-600">Average of {scores.length} scored dimensions. Tap any bar below for the evidence behind it.</p>
            </div>
          </div>
          <div className="mt-4 space-y-4">
            {DIMENSIONS.map((d) => <Row key={d.key} label={d.label} data={health?.[d.key]} testId={d.testId} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeHealth;
